// Marketing dashboard. Six eyelash KPI tiles + This Week + GA4 channel
// split + projects placeholder + alerts. Every number is pulled from an
// existing source of truth:
//   - sessions:           GA4 traffic report (follows the range control)
//   - subscribers:        ActiveCampaign summary
//   - booked calls:       GA4 schedule_call conversions (range-scoped)
//   - marketing prospects/sourced won: PipelineContext prospects
//   - CAC + LTV:          budgetMath (identical math to BudgetView)
// Wins-by-source moved to the Attribution screen's source table in Phase 2.

import { useEffect, useMemo } from 'react';
import { useContent } from '../../../context/ContentContext';
import { usePipeline } from '../../../context/PipelineContext';
import { useClients } from '../../../context/ClientsContext';
import { useFinancialData } from '../../../context/FinancialDataContext';
import { useMarketing } from '../MarketingContext';
import { combineBudgetSummary, computeLtvStats } from '../budgetMath';
import { computeWonAttribution, computeMarketingProspects } from '../attributionMath';
import { formatCurrency } from '../../../utils/formatting';
import KpiTile from './KpiTile';
import ThisWeekCard from './ThisWeekCard';
import ChannelSplitCard from '../attribution/ChannelSplitCard';
import ProjectsSummaryCard from './ProjectsSummaryCard';
import AlertsCard from './AlertsCard';
import { usePace } from './usePace';

// Tile-sized currency: $48.2k above $10k, full dollars below.
function compactCurrency(v) {
  if (v == null) return '—';
  if (Math.abs(v) >= 10000) return `$${(v / 1000).toFixed(1)}k`;
  return formatCurrency(v);
}

export default function DashboardView() {
  const { entries } = useContent();
  const { prospects } = usePipeline();
  const { clients } = useClients();
  const { editableByClass } = useFinancialData();
  const { range, summary, ac, gsc, traffic, funnel } = useMarketing();

  const bwmPL = editableByClass?.bwm;
  const combined = useMemo(
    () => combineBudgetSummary(summary.data, bwmPL),
    [summary.data, bwmPL]
  );
  const year = summary.data?.year ?? new Date().getFullYear();
  const attribution = useMemo(() => computeWonAttribution(prospects, year), [prospects, year]);
  const mktg = useMemo(() => computeMarketingProspects(prospects), [prospects]);
  const ltv = useMemo(
    () => computeLtvStats(clients, combined?.cac?.totalCac),
    [clients, combined]
  );
  const pace = usePace();

  // The tile's deal count must equal the API's wonCount (same rows, two
  // paths). If they drift, the isBwmProspect mirror in attributionMath
  // has fallen out of sync with the server filter — surface it loudly.
  useEffect(() => {
    if (summary.data && prospects?.length && attribution.deals !== summary.data.wonCount) {
      console.warn(
        `[marketing] sourced-won reconciliation mismatch: dashboard counts ${attribution.deals} BWM wins, /marketing/summary reports ${summary.data.wonCount}`
      );
    }
  }, [summary.data, prospects, attribution.deals]);

  // ── tile inputs ──
  const rangeLabel = range === 'qtd' ? 'QTD' : range;

  const traf = traffic.data && traffic.data.configured !== false ? traffic.data : null;
  const sessions = traf?.totals?.sessions;
  const priorSessions = traf?.priorTotals?.sessions;
  const sessionsDeltaPct = sessions != null && priorSessions > 0
    ? Math.round(((sessions - priorSessions) / priorSessions) * 100)
    : null;

  const acData = ac.data && ac.data.configured !== false ? ac.data : null;
  // AC serves 7d and 30d windows; QTD falls back to the 30d cut.
  const acWindow = range === '7d' ? acData?.weekly : acData?.monthly;

  const fun = funnel.data && funnel.data.configured !== false ? funnel.data : null;
  const callsDelta = fun ? fun.calls - fun.prior.calls : null;

  const totalCac = combined?.cac?.totalCac ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3.5">
        <KpiTile
          lash="blue"
          label="Website sessions"
          loading={traffic.loading}
          value={sessions != null ? sessions.toLocaleString() : '—'}
          delta={sessionsDeltaPct != null ? `${Math.abs(sessionsDeltaPct)}%` : null}
          deltaDir={sessionsDeltaPct > 0 ? 'up' : sessionsDeltaPct < 0 ? 'down' : 'flat'}
          foot={traf ? `vs prev ${rangeLabel}` : 'GA4 unavailable'}
        />
        <KpiTile
          lash="teal"
          label="New subscribers"
          loading={ac.loading}
          value={acWindow ? acWindow.new.toLocaleString() : '—'}
          delta={acWindow?.delta != null ? `${acWindow.delta >= 0 ? '+' : ''}${acWindow.delta}` : null}
          deltaDir={acWindow?.delta > 0 ? 'up' : acWindow?.delta < 0 ? 'down' : 'flat'}
          foot={acData ? `${acData.total?.toLocaleString()} total` : 'AC not configured'}
        />
        <KpiTile
          lash="sky"
          label="Booked calls"
          loading={funnel.loading}
          value={fun ? fun.calls.toLocaleString() : '—'}
          delta={callsDelta != null ? `${callsDelta >= 0 ? '+' : ''}${callsDelta}` : null}
          deltaDir={callsDelta > 0 ? 'up' : callsDelta < 0 ? 'down' : 'flat'}
          foot={fun ? `last ${rangeLabel} · GA4` : 'GA4 unavailable'}
        />
        <KpiTile
          lash="navy"
          label="Mktg prospects"
          value={mktg.open.toLocaleString()}
          delta={mktg.newThisMonth > 0 ? `+${mktg.newThisMonth}` : null}
          deltaDir={mktg.newThisMonth > 0 ? 'up' : 'flat'}
          foot="open · new this mo"
        />
        <KpiTile
          lash="teal"
          label="Sourced won"
          value={compactCurrency(attribution.totalValue)}
          foot={`${attribution.deals} ${attribution.deals === 1 ? 'deal' : 'deals'} · ${year}`}
        />
        <KpiTile
          lash="amber"
          label="CAC"
          loading={summary.loading}
          value={totalCac != null ? formatCurrency(totalCac) : '—'}
          delta={ltv.ratio != null ? `${ltv.ratio.toFixed(1)}×` : null}
          deltaDir={ltv.ratio != null ? (ltv.ratio >= 3 ? 'up' : 'down') : null}
          foot={totalCac != null ? 'LTV:CAC' : 'needs a win'}
        />
      </div>

      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <ThisWeekCard />
        <ChannelSplitCard traffic={traffic} compact />
      </div>

      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <ProjectsSummaryCard />
        <AlertsCard entries={entries} gsc={gsc.data} ac={ac.data} pace={pace} />
      </div>
    </div>
  );
}
